/**
 * CaptionsTranslationPageV2 — ports the prototype's
 * `.screen-captions-translation` (cms-redesign-mockup.html ~9412+).
 *
 * Two stacked sections, scoped to a single target language picked
 * via the language SegmentedControl in the header toolbar:
 *
 *   1. Glossary       — source term → target term map. Each row has
 *                       a tinted category chip, optional notes line,
 *                       an active toggle, and edit/delete.
 *   2. Instructions   — free-form prompt snippets (tone, brand
 *                       protection, style, formality) sorted by
 *                       sort_order. Same active toggle + edit/delete
 *                       pattern.
 *
 * Add / edit dialogs stay MUI — too domain-specific to rebuild here
 * (multi-field forms with category selects, sort_order numeric, etc.).
 * Active toggles use the per-id in-flight guard pattern from the
 * v1 page so rapid clicks don't race PUTs.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch as MuiSwitch,
  TextField,
} from '@mui/material';
import Swal from 'sweetalert2';
import {
  deleteTranslationGlossaryTerm,
  deleteTranslationInstruction,
  fetchTranslationGlossary,
  fetchTranslationInstructions,
  saveTranslationGlossaryTerm,
  saveTranslationInstruction,
} from '../../../actions/captionsActions';
import {
  PageHeader,
  Button,
  SegmentedControl,
  LibTable,
  IconButton,
  cn,
} from '../../redesign';
import { LangChip } from '../../Captions/v2/captionsPrimitives';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>;
const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);
const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/* ─── Constants ────────────────────────────────────────────────────── */

const SUPPORTED_LANGUAGES = [{ value: 'es', label: 'Spanish (es)' }];

const GLOSSARY_CATEGORIES = [
  { value: 'tapping_point', label: 'Tapping point' },
  { value: 'setup_phrase',  label: 'Setup phrase' },
  { value: 'technical',     label: 'Technical' },
  { value: 'brand',         label: 'Brand' },
];

const INSTRUCTION_CATEGORIES = [
  { value: 'tone',             label: 'Tone' },
  { value: 'brand_protection', label: 'Brand protection' },
  { value: 'style',            label: 'Style' },
  { value: 'formality',        label: 'Formality' },
];

const TINT = {
  tapping_point:    { bg: '#D6EBE3', color: '#5C7E72' },
  setup_phrase:     { bg: '#DCE7F5', color: '#3A4F7A' },
  technical:        { bg: '#E5F0EC', color: '#5C7E72' },
  brand:            { bg: '#FCEAD1', color: '#8C5A1E' },
  tone:             { bg: '#DCE7F5', color: '#3A4F7A' },
  brand_protection: { bg: '#FCEAD1', color: '#8C5A1E' },
  style:            { bg: '#D6EBE3', color: '#5C7E72' },
  formality:        { bg: '#E58A6A26', color: '#C4715B' },
};

function CategoryChip({ category, options }) {
  const tint = TINT[category] || { bg: '#FAF7F1', color: '#6B6470' };
  const label = options.find((c) => c.value === category)?.label || category;
  return (
    <span
      className="inline-flex items-center font-condensed font-bold uppercase whitespace-nowrap"
      style={{
        fontSize: 10,
        letterSpacing: '0.08em',
        padding: '3px 9px',
        borderRadius: 999,
        background: tint.bg,
        color: tint.color,
      }}
    >
      {label}
    </span>
  );
}

const blankGlossary = (languageCode) => ({
  language_code: languageCode,
  source_term: '',
  target_term: '',
  category: 'technical',
  notes: '',
  is_active: true,
});

const blankInstruction = (languageCode) => ({
  language_code: languageCode,
  instruction: '',
  category: 'tone',
  sort_order: 100,
  is_active: true,
});

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function CaptionsTranslationPageV2() {
  const dispatch = useDispatch();
  const glossary = useSelector((s) => s.captions.translationGlossary);
  const instructions = useSelector((s) => s.captions.translationInstructions);
  const isLoading = glossary === null || instructions === null;

  const [language, setLanguage] = useState(SUPPORTED_LANGUAGES[0].value);

  const [glossaryDialogOpen, setGlossaryDialogOpen] = useState(false);
  const [glossaryDraft, setGlossaryDraft] = useState(() => blankGlossary(SUPPORTED_LANGUAGES[0].value));
  const [glossarySubmitting, setGlossarySubmitting] = useState(false);

  const [instructionDialogOpen, setInstructionDialogOpen] = useState(false);
  const [instructionDraft, setInstructionDraft] = useState(() => blankInstruction(SUPPORTED_LANGUAGES[0].value));
  const [instructionSubmitting, setInstructionSubmitting] = useState(false);

  const [togglingGlossaryIds, setTogglingGlossaryIds] = useState(() => new Set());
  const [togglingInstructionIds, setTogglingInstructionIds] = useState(() => new Set());

  useEffect(() => {
    dispatch(fetchTranslationGlossary({ language }));
    dispatch(fetchTranslationInstructions({ language }));
  }, [dispatch, language]);

  const glossaryRows = useMemo(() => {
    if (!Array.isArray(glossary)) return [];
    return glossary
      .filter((g) => g.language_code === language)
      .slice()
      .sort((a, b) => {
        const cat = String(a.category ?? '').localeCompare(String(b.category ?? ''));
        if (cat !== 0) return cat;
        return String(a.source_term ?? '').localeCompare(String(b.source_term ?? ''));
      });
  }, [glossary, language]);

  const instructionRows = useMemo(() => {
    if (!Array.isArray(instructions)) return [];
    return instructions
      .filter((i) => i.language_code === language)
      .slice()
      .sort((a, b) => {
        const orderDiff = (a.sort_order ?? 0) - (b.sort_order ?? 0);
        if (orderDiff !== 0) return orderDiff;
        return (a.id ?? 0) - (b.id ?? 0);
      });
  }, [instructions, language]);

  /* ─── Glossary handlers ────────────────────────────────────────── */

  const openCreateGlossary = () => {
    setGlossaryDraft(blankGlossary(language));
    setGlossaryDialogOpen(true);
  };
  const openEditGlossary = (term) => {
    setGlossaryDraft({ ...term });
    setGlossaryDialogOpen(true);
  };
  const onSaveGlossary = () => {
    if (glossarySubmitting) return;
    if (!glossaryDraft.source_term.trim() || !glossaryDraft.target_term.trim()) {
      Swal.fire({ icon: 'warning', title: 'Both source and target are required.', timer: 1500 });
      return;
    }
    const payload = {
      ...glossaryDraft,
      source_term: glossaryDraft.source_term.trim(),
      target_term: glossaryDraft.target_term.trim(),
      notes: glossaryDraft.notes?.trim() || null,
    };
    setGlossarySubmitting(true);
    dispatch(
      saveTranslationGlossaryTerm(payload, () => setGlossaryDialogOpen(false)),
    ).finally(() => setGlossarySubmitting(false));
  };
  const onToggleGlossaryActive = (term) => {
    if (togglingGlossaryIds.has(term.id)) return;
    setTogglingGlossaryIds((prev) => {
      const next = new Set(prev);
      next.add(term.id);
      return next;
    });
    dispatch(saveTranslationGlossaryTerm({ ...term, is_active: !term.is_active }, null, { silent: true }))
      .catch(() => {})
      .finally(() => {
        setTogglingGlossaryIds((prev) => {
          const next = new Set(prev);
          next.delete(term.id);
          return next;
        });
      });
  };
  const onDeleteGlossary = async (term) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: term.is_active ? 'Deactivate this entry?' : 'Permanently delete this entry?',
      text: term.is_active
        ? "The entry will be removed from the translator's prompt but kept on file for re-activation."
        : "This entry is already inactive. Permanently deleting it removes the row — there's no undo.",
      showCancelButton: true,
      confirmButtonText: term.is_active ? 'Deactivate' : 'Delete forever',
      confirmButtonColor: term.is_active ? undefined : '#d33',
    });
    if (!isConfirmed) return;
    dispatch(deleteTranslationGlossaryTerm(term.id, null, { force: !term.is_active }));
  };

  /* ─── Instruction handlers ─────────────────────────────────────── */

  const openCreateInstruction = () => {
    setInstructionDraft(blankInstruction(language));
    setInstructionDialogOpen(true);
  };
  const openEditInstruction = (inst) => {
    setInstructionDraft({ ...inst });
    setInstructionDialogOpen(true);
  };
  const onSaveInstruction = () => {
    if (instructionSubmitting) return;
    if (!instructionDraft.instruction.trim()) {
      Swal.fire({ icon: 'warning', title: 'Instruction text is required.', timer: 1500 });
      return;
    }
    const payload = {
      ...instructionDraft,
      instruction: instructionDraft.instruction.trim(),
      sort_order: Number.isFinite(Number(instructionDraft.sort_order))
        ? Number(instructionDraft.sort_order)
        : 0,
    };
    setInstructionSubmitting(true);
    dispatch(
      saveTranslationInstruction(payload, () => setInstructionDialogOpen(false)),
    ).finally(() => setInstructionSubmitting(false));
  };
  const onToggleInstructionActive = (inst) => {
    if (togglingInstructionIds.has(inst.id)) return;
    setTogglingInstructionIds((prev) => {
      const next = new Set(prev);
      next.add(inst.id);
      return next;
    });
    dispatch(saveTranslationInstruction({ ...inst, is_active: !inst.is_active }, null, { silent: true }))
      .catch(() => {})
      .finally(() => {
        setTogglingInstructionIds((prev) => {
          const next = new Set(prev);
          next.delete(inst.id);
          return next;
        });
      });
  };
  const onDeleteInstruction = async (inst) => {
    const { isConfirmed } = await Swal.fire({
      icon: 'question',
      title: inst.is_active ? 'Deactivate this instruction?' : 'Permanently delete this instruction?',
      text: inst.is_active
        ? "The instruction will be removed from the translator's prompt but kept on file."
        : 'This instruction is already inactive. Permanently deleting removes the row — no undo.',
      showCancelButton: true,
      confirmButtonText: inst.is_active ? 'Deactivate' : 'Delete forever',
      confirmButtonColor: inst.is_active ? undefined : '#d33',
    });
    if (!isConfirmed) return;
    dispatch(deleteTranslationInstruction(inst.id, null, { force: !inst.is_active }));
  };

  const totalLabel = `${(glossaryRows?.length ?? 0) + (instructionRows?.length ?? 0)} entries`;

  return (
    <div className="bg-bg min-h-full">
      <PageHeader
        eyebrow="Captions"
        title="Translation"
        total={totalLabel}
        actions={
          <span className="inline-flex items-center" style={{ gap: 8 }}>
            <span className="text-text-muted font-mono" style={{ fontSize: 12 }}>
              Language
            </span>
            <SegmentedControl
              value={language}
              onChange={setLanguage}
              options={SUPPORTED_LANGUAGES.map((l) => ({
                value: l.value,
                label: l.label,
                icon: <LangChip code={l.value} />,
              }))}
            />
          </span>
        }
      />

      {isLoading ? (
        <div
          className="flex items-center justify-center text-text-muted"
          style={{ height: 256 }}
        >
          Loading translation settings…
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          {/* ─── Glossary section ─────────────────────────────────── */}
          <section style={{ marginBottom: 32 }}>
            <header
              className="flex items-baseline border-b border-sand"
              style={{ gap: 10, marginBottom: 10, paddingBottom: 8 }}
            >
              <h2 className="font-bold text-navy" style={{ fontSize: 18, margin: 0 }}>
                Glossary
              </h2>
              <span
                className="font-mono text-text-muted bg-cream border border-sand inline-flex items-center"
                style={{ fontSize: 11, padding: '2px 9px', borderRadius: 999 }}
              >
                {glossaryRows.length}
              </span>
              <span className="ml-auto text-text-muted" style={{ fontSize: 12 }}>
                Source term → target term map
              </span>
              <Button variant="coral" leadingIcon={PlusSign} onClick={openCreateGlossary}>
                Add Term
              </Button>
            </header>
            {glossaryRows.length === 0 ? (
              <div
                className="bg-surface border border-sand text-text-muted italic text-center"
                style={{ borderRadius: 12, padding: '30px 16px', fontSize: 13 }}
              >
                No glossary entries for this language.
              </div>
            ) : (
              <LibTable
                fixed
                className="border border-sand"
                style={{ borderRadius: 12, overflow: 'hidden' }}
              >
                <colgroup>
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '24%' }} />
                  <col style={{ width: '16%' }} />
                  <col />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <LibTable.Head>
                  <tr>
                    <LibTable.Th>Source</LibTable.Th>
                    <LibTable.Th>Target</LibTable.Th>
                    <LibTable.Th>Category</LibTable.Th>
                    <LibTable.Th>Notes</LibTable.Th>
                    <LibTable.CenterTh>Active</LibTable.CenterTh>
                    <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
                  </tr>
                </LibTable.Head>
                <tbody>
                  {glossaryRows.map((term) => (
                    <LibTable.Row key={term.id} inactive={!term.is_active}>
                      <LibTable.Td>
                        <span
                          className={cn(
                            'font-semibold truncate block',
                            term.is_active ? 'text-navy' : 'text-text-light italic',
                          )}
                          style={{ fontSize: 13.5 }}
                          title={term.source_term}
                        >
                          {term.source_term}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <span
                          className={cn(
                            'truncate block',
                            term.is_active ? 'text-navy' : 'text-text-light italic',
                          )}
                          style={{ fontSize: 13.5 }}
                          title={term.target_term}
                        >
                          {term.target_term}
                        </span>
                      </LibTable.Td>
                      <LibTable.Td>
                        <CategoryChip category={term.category} options={GLOSSARY_CATEGORIES} />
                      </LibTable.Td>
                      <LibTable.Td>
                        <span className="text-text-muted truncate block" style={{ fontSize: 12.5 }}>
                          {term.notes || '—'}
                        </span>
                      </LibTable.Td>
                      <LibTable.CenterTd>
                        <MuiSwitch
                          size="small"
                          checked={!!term.is_active}
                          disabled={togglingGlossaryIds.has(term.id)}
                          onChange={() => onToggleGlossaryActive(term)}
                        />
                      </LibTable.CenterTd>
                      <LibTable.Td style={{ textAlign: 'right' }}>
                        <div className="inline-flex" style={{ gap: 4 }}>
                          <IconButton size="sm" aria-label="Edit" onClick={() => openEditGlossary(term)}>
                            {EditIconSvg}
                          </IconButton>
                          <IconButton size="sm" aria-label="Delete" onClick={() => onDeleteGlossary(term)}>
                            {TrashIconSvg}
                          </IconButton>
                        </div>
                      </LibTable.Td>
                    </LibTable.Row>
                  ))}
                </tbody>
              </LibTable>
            )}
          </section>

          {/* ─── Instructions section ─────────────────────────────── */}
          <section>
            <header
              className="flex items-baseline border-b border-sand"
              style={{ gap: 10, marginBottom: 10, paddingBottom: 8 }}
            >
              <h2 className="font-bold text-navy" style={{ fontSize: 18, margin: 0 }}>
                Instructions
              </h2>
              <span
                className="font-mono text-text-muted bg-cream border border-sand inline-flex items-center"
                style={{ fontSize: 11, padding: '2px 9px', borderRadius: 999 }}
              >
                {instructionRows.length}
              </span>
              <span className="ml-auto text-text-muted" style={{ fontSize: 12 }}>
                Prompt snippets (sorted by sort_order)
              </span>
              <Button variant="coral" leadingIcon={PlusSign} onClick={openCreateInstruction}>
                Add Instruction
              </Button>
            </header>
            {instructionRows.length === 0 ? (
              <div
                className="bg-surface border border-sand text-text-muted italic text-center"
                style={{ borderRadius: 12, padding: '30px 16px', fontSize: 13 }}
              >
                No translation instructions for this language.
              </div>
            ) : (
              <LibTable
                fixed
                className="border border-sand"
                style={{ borderRadius: 12, overflow: 'hidden' }}
              >
                <colgroup>
                  <col style={{ width: 70 }} />
                  <col style={{ width: '18%' }} />
                  <col />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                </colgroup>
                <LibTable.Head>
                  <tr>
                    <LibTable.NumTh>Order</LibTable.NumTh>
                    <LibTable.Th>Category</LibTable.Th>
                    <LibTable.Th>Instruction</LibTable.Th>
                    <LibTable.CenterTh>Active</LibTable.CenterTh>
                    <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
                  </tr>
                </LibTable.Head>
                <tbody>
                  {instructionRows.map((inst) => (
                    <LibTable.Row key={inst.id} inactive={!inst.is_active}>
                      <LibTable.NumTd>
                        <span className="font-mono text-text-muted" style={{ fontSize: 12 }}>
                          {inst.sort_order ?? 0}
                        </span>
                      </LibTable.NumTd>
                      <LibTable.Td>
                        <CategoryChip category={inst.category} options={INSTRUCTION_CATEGORIES} />
                      </LibTable.Td>
                      <LibTable.Td>
                        <span
                          className={cn(
                            inst.is_active ? 'text-navy' : 'text-text-light italic',
                          )}
                          style={{ fontSize: 13, lineHeight: 1.4 }}
                          title={inst.instruction}
                        >
                          {inst.instruction}
                        </span>
                      </LibTable.Td>
                      <LibTable.CenterTd>
                        <MuiSwitch
                          size="small"
                          checked={!!inst.is_active}
                          disabled={togglingInstructionIds.has(inst.id)}
                          onChange={() => onToggleInstructionActive(inst)}
                        />
                      </LibTable.CenterTd>
                      <LibTable.Td style={{ textAlign: 'right' }}>
                        <div className="inline-flex" style={{ gap: 4 }}>
                          <IconButton size="sm" aria-label="Edit" onClick={() => openEditInstruction(inst)}>
                            {EditIconSvg}
                          </IconButton>
                          <IconButton size="sm" aria-label="Delete" onClick={() => onDeleteInstruction(inst)}>
                            {TrashIconSvg}
                          </IconButton>
                        </div>
                      </LibTable.Td>
                    </LibTable.Row>
                  ))}
                </tbody>
              </LibTable>
            )}
          </section>
        </div>
      )}

      {/* ─── Glossary dialog ─────────────────────────────────────── */}
      <Dialog open={glossaryDialogOpen} onClose={() => setGlossaryDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{glossaryDraft.id ? 'Edit glossary term' : 'Add glossary term'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Source term"
              value={glossaryDraft.source_term}
              onChange={(e) => setGlossaryDraft({ ...glossaryDraft, source_term: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <TextField
              label="Target term"
              value={glossaryDraft.target_term}
              onChange={(e) => setGlossaryDraft({ ...glossaryDraft, target_term: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel id="gloss-cat-label">Category</InputLabel>
              <Select
                labelId="gloss-cat-label"
                label="Category"
                value={glossaryDraft.category}
                onChange={(e) => setGlossaryDraft({ ...glossaryDraft, category: e.target.value })}
              >
                {GLOSSARY_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Notes"
              value={glossaryDraft.notes || ''}
              onChange={(e) => setGlossaryDraft({ ...glossaryDraft, notes: e.target.value })}
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <MuiSwitch
                  checked={!!glossaryDraft.is_active}
                  onChange={(e) => setGlossaryDraft({ ...glossaryDraft, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setGlossaryDialogOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={onSaveGlossary} disabled={glossarySubmitting}>
            {glossarySubmitting ? 'Saving…' : 'Save term'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Instruction dialog ──────────────────────────────────── */}
      <Dialog open={instructionDialogOpen} onClose={() => setInstructionDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{instructionDraft.id ? 'Edit instruction' : 'Add instruction'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Instruction text"
              value={instructionDraft.instruction}
              onChange={(e) => setInstructionDraft({ ...instructionDraft, instruction: e.target.value })}
              fullWidth
              required
              autoFocus
              multiline
              minRows={3}
            />
            <FormControl fullWidth>
              <InputLabel id="inst-cat-label">Category</InputLabel>
              <Select
                labelId="inst-cat-label"
                label="Category"
                value={instructionDraft.category}
                onChange={(e) => setInstructionDraft({ ...instructionDraft, category: e.target.value })}
              >
                {INSTRUCTION_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Sort order"
              type="number"
              value={instructionDraft.sort_order}
              onChange={(e) => setInstructionDraft({ ...instructionDraft, sort_order: e.target.value })}
              fullWidth
              helperText="Lower numbers come first in the prompt."
            />
            <FormControlLabel
              control={
                <MuiSwitch
                  checked={!!instructionDraft.is_active}
                  onChange={(e) => setInstructionDraft({ ...instructionDraft, is_active: e.target.checked })}
                />
              }
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={() => setInstructionDialogOpen(false)}>Cancel</Button>
          <Button variant="primary" onClick={onSaveInstruction} disabled={instructionSubmitting}>
            {instructionSubmitting ? 'Saving…' : 'Save instruction'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
